/**
 * CalendarEventsView — Calendar events with agenda/week/day views and accept/decline/tentative actions.
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Calendar,
  MapPin,
  Video,
  Users,
  Clock,
  Check,
  X,
  HelpCircle,
  ExternalLink,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Loader2,
  List,
  LayoutGrid,
  Plus,
  Link2,
  Trash2,
  CheckCircle2,
  AlertCircle,
  Bell,
} from 'lucide-react';
import { apiClient } from '@/services/apiClient';

interface Activity {
  id: string;
  type: string;
  title: string;
  description?: string;
  direction?: string;
  activityDate?: string;
  createdAt?: string;
  opportunityId?: string;
  organizationId?: number;
  metadata?: {
    event_type?: string;
    provider?: string;
    organizer_email?: string;
    organizer_name?: string;
    attendees?: Array<{ email: string; name: string; response: string; type: string }>;
    location?: string;
    meeting_link?: string;
    show_as?: string;
    my_response?: string;
    is_organizer?: boolean;
    has_attachments?: boolean;
    importance?: string;
    categories?: string[];
    web_link?: string;
    start_timezone?: string;
    end_timezone?: string;
  };
}

interface Opportunity {
  id: string;
  name: string;
  company: string;
  stage: string;
  amount: number;
}

interface CalendarEventsViewProps {
  activities: Activity[];
  opportunities: Opportunity[];
  onRefreshActivities: () => void;
}

type ViewMode = 'agenda' | 'week' | 'day';

// Date helpers
const isSameDay = (a: Date, b: Date) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const startOfWeek = (d: Date) => { const r = new Date(d); r.setDate(r.getDate() - r.getDay()); r.setHours(0,0,0,0); return r; };
const addDays = (d: Date, n: number) => { const r = new Date(d); r.setDate(r.getDate() + n); return r; };
const formatDate = (d: Date) => d.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
const formatTime = (d: Date) => d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
const HOURS = Array.from({ length: 15 }, (_, i) => i + 7); // 7am - 9pm

// Meeting provider options (static fallback)
const MEETING_PROVIDERS = [
  { id: 'teams', label: 'Microsoft Teams' },
  { id: 'jitsi', label: 'Jitsi Meet' },
  { id: 'zoom', label: 'Zoom' },
  { id: 'google_meet', label: 'Google Meet' },
] as const;

interface MeetingProviderStatus {
  id: string;
  label: string;
  connected: boolean;
  auto_create: boolean;
  needs_link: boolean;
  connect_hint?: string | null;
}

interface CreateEventModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void;
  defaultDate?: Date;
}

function CreateEventModal({ isOpen, onClose, onCreated, defaultDate }: CreateEventModalProps) {
  const now = defaultDate || new Date();
  const defaultStart = new Date(now);
  defaultStart.setMinutes(Math.ceil(defaultStart.getMinutes() / 30) * 30, 0, 0);
  const defaultEnd = new Date(defaultStart);
  defaultEnd.setHours(defaultEnd.getHours() + 1);

  const toLocalISOString = (d: Date) => {
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const [subject, setSubject] = useState('');
  const [startTime, setStartTime] = useState(toLocalISOString(defaultStart));
  const [endTime, setEndTime] = useState(toLocalISOString(defaultEnd));
  const [location, setLocation] = useState('');
  const [bodyContent, setBodyContent] = useState('');
  const [attendeeInput, setAttendeeInput] = useState('');
  const [attendees, setAttendees] = useState<string[]>([]);
  const [meetingProvider, setMeetingProvider] = useState<string>('none');
  const [externalMeetingLink, setExternalMeetingLink] = useState('');
  const [jitsiRoomName, setJitsiRoomName] = useState('');
  const [importance, setImportance] = useState('normal');
  const [isAllDay, setIsAllDay] = useState(false);
  const [reminderMinutes, setReminderMinutes] = useState(15);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [providerStatuses, setProviderStatuses] = useState<MeetingProviderStatus[]>([]);

  // Fetch meeting provider connection statuses when modal opens
  useEffect(() => {
    if (!isOpen) return;
    apiClient.get('/api/crm-activities/calendar-events/meeting-providers')
      .then(res => setProviderStatuses(res.data?.providers || []))
      .catch(() => {}); // Silently fail — fallback to manual link mode
  }, [isOpen]);

  const getProviderStatus = (id: string): MeetingProviderStatus | undefined =>
    providerStatuses.find(p => p.id === id);

  const addAttendee = () => {
    const email = attendeeInput.trim();
    if (email && email.includes('@') && !attendees.includes(email)) {
      setAttendees(prev => [...prev, email]);
      setAttendeeInput('');
    }
  };

  const removeAttendee = (email: string) => {
    setAttendees(prev => prev.filter(a => a !== email));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addAttendee();
    }
  };

  const handleCreate = async () => {
    if (!subject.trim()) { setError('Subject is required'); return; }
    if (!startTime || !endTime) { setError('Start and end times are required'); return; }
    setCreating(true);
    setError('');

    const isTeams = meetingProvider === 'teams';
    let meetingLink = '';
    if (meetingProvider === 'jitsi') {
      const room = jitsiRoomName.trim() || subject.replace(/\s+/g, '-').toLowerCase();
      meetingLink = `https://meet.jit.si/${room}`;
    } else if (meetingProvider === 'zoom' || meetingProvider === 'google_meet') {
      const status = getProviderStatus(meetingProvider);
      if (status?.auto_create) {
        // Backend will auto-create the meeting link — no paste needed
        meetingLink = '';
      } else {
        meetingLink = externalMeetingLink.trim();
      }
    }

    // Get user's timezone
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

    try {
      await apiClient.post('/api/crm-activities/calendar-events/create', {
        subject: subject.trim(),
        start_time: startTime,
        end_time: endTime,
        timezone: tz,
        location: location.trim() || undefined,
        body_content: bodyContent.trim() || undefined,
        attendees: attendees.length > 0 ? attendees : undefined,
        is_online_meeting: isTeams,
        meeting_link: meetingLink || undefined,
        meeting_provider: meetingProvider !== 'none' ? meetingProvider : undefined,
        importance,
        is_all_day: isAllDay,
        reminder_minutes: reminderMinutes,
      });
      onCreated();
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to create event');
    } finally {
      setCreating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-2 sm:mx-4 max-h-[90vh] sm:max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 sm:px-5 py-3 sm:py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
          <h3 className="text-base font-semibold text-gray-900">Create Calendar Event</h3>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded"><X className="h-4 w-4" /></button>
        </div>
        <div className="px-4 sm:px-5 py-4 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-3 py-2">{error}</div>
          )}

          {/* Subject */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Subject *</label>
            <input
              type="text" value={subject} onChange={e => setSubject(e.target.value)}
              placeholder="Meeting with..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Date/Time */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Start *</label>
              <input
                type={isAllDay ? 'date' : 'datetime-local'} value={isAllDay ? startTime.split('T')[0] : startTime}
                onChange={e => setStartTime(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">End *</label>
              <input
                type={isAllDay ? 'date' : 'datetime-local'} value={isAllDay ? endTime.split('T')[0] : endTime}
                onChange={e => setEndTime(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={isAllDay} onChange={e => setIsAllDay(e.target.checked)} className="rounded" />
              All day
            </label>
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-500">Reminder:</label>
              <select value={reminderMinutes} onChange={e => setReminderMinutes(Number(e.target.value))}
                className="px-2 py-1 text-xs border border-gray-300 rounded">
                <option value={0}>None</option>
                <option value={5}>5 min</option>
                <option value={15}>15 min</option>
                <option value={30}>30 min</option>
                <option value={60}>1 hour</option>
                <option value={1440}>1 day</option>
              </select>
            </div>
          </div>

          {/* Location */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Location</label>
            <input
              type="text" value={location} onChange={e => setLocation(e.target.value)}
              placeholder="Conference room, address..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Meeting Provider */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Online Meeting</label>
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-1">
              <button onClick={() => setMeetingProvider('none')}
                className={`px-2 py-1.5 text-xs rounded-lg border font-medium ${meetingProvider === 'none' ? 'bg-gray-100 border-gray-400 text-gray-700' : 'border-gray-200 text-gray-500 hover:bg-gray-50'}`}>
                None
              </button>
              {MEETING_PROVIDERS.map(p => {
                const status = getProviderStatus(p.id);
                const isConnected = status?.connected ?? (p.id === 'jitsi');
                return (
                  <button key={p.id} onClick={() => setMeetingProvider(p.id)}
                    className={`px-2 py-1.5 text-xs rounded-lg border font-medium relative ${meetingProvider === p.id ? 'bg-blue-50 border-blue-300 text-blue-700' : 'border-gray-200 text-gray-500 hover:bg-gray-50'}`}>
                    {p.label.split(' ').pop()}
                    {isConnected && (
                      <CheckCircle2 className="h-3 w-3 text-green-500 absolute -top-1 -right-1" />
                    )}
                  </button>
                );
              })}
            </div>

            {meetingProvider === 'teams' && (
              <p className="text-xs text-green-600 mt-1.5 flex items-center gap-1">
                <CheckCircle2 className="h-3 w-3" /> Teams meeting link will be auto-generated
              </p>
            )}
            {meetingProvider === 'jitsi' && (
              <div className="mt-2">
                <p className="text-xs text-green-600 mb-1 flex items-center gap-1">
                  <CheckCircle2 className="h-3 w-3" /> Jitsi link will be auto-generated (no account needed)
                </p>
                <label className="text-xs text-gray-500">Room name (optional — auto-generated from subject)</label>
                <input type="text" value={jitsiRoomName} onChange={e => setJitsiRoomName(e.target.value)}
                  placeholder="my-meeting-room"
                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              </div>
            )}
            {(meetingProvider === 'zoom' || meetingProvider === 'google_meet') && (() => {
              const status = getProviderStatus(meetingProvider);
              const isConnected = status?.auto_create;
              const providerName = meetingProvider === 'zoom' ? 'Zoom' : 'Google Meet';
              return (
                <div className="mt-2">
                  {isConnected ? (
                    <p className="text-xs text-green-600 flex items-center gap-1">
                      <CheckCircle2 className="h-3 w-3" /> {providerName} meeting link will be auto-generated
                    </p>
                  ) : (
                    <>
                      <div className="flex items-center gap-1 mb-1">
                        <AlertCircle className="h-3 w-3 text-amber-500" />
                        <span className="text-xs text-amber-600">
                          {status?.connect_hint || `Connect ${providerName} in Settings → Integrations for auto-generated links`}
                        </span>
                      </div>
                      <label className="text-xs text-gray-500">Or paste your {providerName} link</label>
                      <input type="url" value={externalMeetingLink} onChange={e => setExternalMeetingLink(e.target.value)}
                        placeholder={meetingProvider === 'zoom' ? 'https://zoom.us/j/...' : 'https://meet.google.com/...'}
                        className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                    </>
                  )}
                </div>
              );
            })()}
          </div>

          {/* Attendees */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Attendees</label>
            <div className="flex gap-2">
              <input
                type="email" value={attendeeInput}
                onChange={e => setAttendeeInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="email@example.com"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <button onClick={addAttendee} className="px-3 py-2 bg-blue-50 text-blue-600 rounded-lg text-sm hover:bg-blue-100 border border-blue-200">
                Add
              </button>
            </div>
            {attendees.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {attendees.map(email => (
                  <span key={email} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-xs">
                    {email}
                    <button onClick={() => removeAttendee(email)} className="hover:text-red-500"><X className="h-3 w-3" /></button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
            <textarea
              value={bodyContent} onChange={e => setBodyContent(e.target.value)}
              rows={3} placeholder="Meeting agenda, notes..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
            />
          </div>

          {/* Importance */}
          <div className="flex items-center gap-3">
            <label className="text-xs font-medium text-gray-600">Priority:</label>
            {(['low', 'normal', 'high'] as const).map(p => (
              <button key={p} onClick={() => setImportance(p)}
                className={`px-3 py-1 text-xs rounded-lg border ${importance === p
                  ? (p === 'high' ? 'bg-red-50 border-red-300 text-red-700' : p === 'low' ? 'bg-gray-50 border-gray-300 text-gray-600' : 'bg-blue-50 border-blue-300 text-blue-700')
                  : 'border-gray-200 text-gray-500 hover:bg-gray-50'
                }`}>
                {p.charAt(0).toUpperCase() + p.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-200 bg-gray-50 rounded-b-xl">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
          <button onClick={handleCreate} disabled={creating || !subject.trim()}
            className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1.5">
            {creating && <Loader2 className="h-4 w-4 animate-spin" />}
            {creating ? 'Creating...' : 'Create Event'}
          </button>
        </div>
      </div>
    </div>
  );
}


export function CalendarEventsView({ activities, opportunities, onRefreshActivities }: CalendarEventsViewProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [responding, setResponding] = useState<string | null>(null);
  const [respondedIds, setRespondedIds] = useState<Set<string>>(new Set());
  const [viewMode, setViewMode] = useState<ViewMode>('agenda');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedEvent, setSelectedEvent] = useState<Activity | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const allEvents = useMemo(() =>
    [...activities].sort((a, b) => {
      const da = a.activityDate ? new Date(a.activityDate).getTime() : 0;
      const db = b.activityDate ? new Date(b.activityDate).getTime() : 0;
      return da - db;
    }),
  [activities]);

  // Pending RSVP: future events where user is not organizer and hasn't responded
  const pendingRsvpEvents = useMemo(() => {
    const now = new Date();
    return allEvents.filter(ev => {
      if (respondedIds.has(ev.id)) return false;
      const meta = ev.metadata || {};
      // Exclude organizer's own events
      if (meta.is_organizer) return false;
      if (ev.direction === 'outbound' && (!meta.attendees || meta.attendees.length === 0)) return false;
      const response = meta.my_response || 'none';
      if (response === 'accepted' || response === 'declined' || response === 'tentativelyAccepted' || response === 'organizer') return false;
      // Only future or today's events
      if (ev.activityDate && new Date(ev.activityDate) < new Date(now.getFullYear(), now.getMonth(), now.getDate())) return false;
      return true;
    });
  }, [allEvents, respondedIds]);

  // Events for current week or day
  const visibleEvents = useMemo(() => {
    if (viewMode === 'agenda') {
      const now = new Date();
      return allEvents.filter(a => a.activityDate ? new Date(a.activityDate) >= new Date(now.getFullYear(), now.getMonth(), now.getDate()) : true);
    }
    if (viewMode === 'day') {
      return allEvents.filter(a => a.activityDate && isSameDay(new Date(a.activityDate), currentDate));
    }
    // week
    const weekStart = startOfWeek(currentDate);
    const weekEnd = addDays(weekStart, 7);
    return allEvents.filter(a => {
      if (!a.activityDate) return false;
      const d = new Date(a.activityDate);
      return d >= weekStart && d < weekEnd;
    });
  }, [allEvents, viewMode, currentDate]);

  const syncCalendar = useCallback(async () => {
    setSyncing(true);
    try {
      const res = await apiClient.post('/api/crm-activities/calendar-sync/meetings');
      const d = res.data || {};
      if (d.synced_count > 0) onRefreshActivities();
      alert(`Synced ${d.synced_count || 0} events (${d.skipped || 0} already synced)`);
    } catch (err: any) {
      alert(err?.response?.data?.detail || err?.response?.data?.message || 'Calendar sync failed');
    } finally {
      setSyncing(false);
    }
  }, [onRefreshActivities]);

  const respondToEvent = useCallback(async (activityId: string, response: 'accept' | 'tentativelyAccept' | 'decline') => {
    setResponding(activityId);
    const responseMap: Record<string, string> = { accept: 'accepted', tentativelyAccept: 'tentativelyAccepted', decline: 'declined' };
    try {
      await apiClient.post(`/api/crm-activities/calendar-events/${activityId}/respond?response=${response}`);
      setRespondedIds(prev => new Set(prev).add(activityId));
      // Update selectedEvent so the modal reflects the new response immediately
      setSelectedEvent(prev => {
        if (prev && prev.id === activityId) {
          return { ...prev, metadata: { ...prev.metadata, my_response: responseMap[response] || response } };
        }
        return prev;
      });
      onRefreshActivities();
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err?.message || 'Failed to respond';
      if (detail.includes("you're the meeting organizer") || detail.includes('organizer')) {
        setRespondedIds(prev => new Set(prev).add(activityId));
        setSelectedEvent(prev => {
          if (prev && prev.id === activityId) {
            return { ...prev, metadata: { ...prev.metadata, is_organizer: true, my_response: 'organizer' } };
          }
          return prev;
        });
      } else {
        alert(detail);
      }
    } finally {
      setResponding(null);
    }
  }, [onRefreshActivities]);

  const responseIcon = (response: string) => {
    switch (response) {
      case 'accepted': return <Check className="h-3 w-3 text-green-500" />;
      case 'declined': return <X className="h-3 w-3 text-red-500" />;
      case 'tentativelyAccepted': return <HelpCircle className="h-3 w-3 text-amber-500" />;
      default: return <Clock className="h-3 w-3 text-gray-400" />;
    }
  };

  const responseLabel = (r: string) => {
    switch (r) {
      case 'accepted': return 'Accepted';
      case 'declined': return 'Declined';
      case 'tentativelyAccepted': return 'Tentative';
      case 'organizer': return 'Organizer';
      default: return 'Pending';
    }
  };

  const responseBg = (r: string) => {
    switch (r) {
      case 'accepted': return 'bg-green-100 border-green-200 text-green-800';
      case 'declined': return 'bg-red-100 border-red-200 text-red-800';
      case 'tentativelyAccepted': return 'bg-amber-100 border-amber-200 text-amber-800';
      default: return 'bg-purple-100 border-purple-200 text-purple-800';
    }
  };

  const navigateDate = (dir: number) => {
    if (viewMode === 'day') setCurrentDate(addDays(currentDate, dir));
    else if (viewMode === 'week') setCurrentDate(addDays(currentDate, dir * 7));
  };

  // Render an event card (used in week/day views and the detail modal)
  const EventCard = ({ event, compact = false }: { event: Activity; compact?: boolean }) => {
    const meta = event.metadata || {};
    const myResponse = meta.my_response || 'none';
    return (
      <div
        className={`rounded-md border cursor-pointer transition-colors hover:shadow-sm ${responseBg(meta.is_organizer ? 'organizer' : myResponse)} ${compact ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-1.5 text-xs'}`}
        onClick={(e) => { e.stopPropagation(); setSelectedEvent(event); }}
        title={event.title}
      >
        <p className={`font-medium truncate ${compact ? '' : 'mb-0.5'}`}>{event.title}</p>
        {!compact && (
          <div className="flex items-center gap-1.5 opacity-80">
            {event.activityDate && <span>{formatTime(new Date(event.activityDate))}</span>}
            {meta.meeting_link && <Video className="h-3 w-3" />}
            {meta.location && <span className="truncate max-w-[100px]">{meta.location}</span>}
          </div>
        )}
      </div>
    );
  };

  // Week view
  const WeekView = () => {
    const weekStart = startOfWeek(currentDate);
    const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

    return (
      <div className="grid grid-cols-[50px_repeat(7,1fr)] sm:grid-cols-[60px_repeat(7,1fr)] border border-gray-200 rounded-lg overflow-hidden min-w-[600px] sm:min-w-[700px]">
        {/* Header row */}
        <div className="bg-gray-50 border-b border-gray-200 p-1" />
        {days.map(day => (
          <div
            key={day.toISOString()}
            className={`border-b border-l border-gray-200 px-1 py-2 text-center text-xs ${
              isSameDay(day, new Date()) ? 'bg-blue-50 font-bold' : 'bg-gray-50'
            }`}
          >
            <div className="text-gray-500">{day.toLocaleDateString([], { weekday: 'short' })}</div>
            <div className={`text-sm ${isSameDay(day, new Date()) ? 'text-blue-600' : 'text-gray-900'}`}>
              {day.getDate()}
            </div>
          </div>
        ))}

        {/* Time rows */}
        {HOURS.map(hour => (
          <React.Fragment key={hour}>
            <div className="border-t border-gray-100 px-1 py-0 text-[10px] text-gray-400 text-right pr-2 h-14 flex items-start pt-0.5">
              {hour === 0 ? '12 AM' : hour < 12 ? `${hour} AM` : hour === 12 ? '12 PM' : `${hour - 12} PM`}
            </div>
            {days.map(day => {
              const dayEvents = allEvents.filter(ev => {
                if (!ev.activityDate) return false;
                const d = new Date(ev.activityDate);
                return isSameDay(d, day) && d.getHours() === hour;
              });
              return (
                <div key={`${day.toISOString()}-${hour}`} className="border-t border-l border-gray-100 h-14 px-0.5 py-0.5 overflow-hidden">
                  {dayEvents.map(ev => (
                    <EventCard key={ev.id} event={ev} compact />
                  ))}
                </div>
              );
            })}
          </React.Fragment>
        ))}
      </div>
    );
  };

  // Day view
  const DayView = () => (
    <div className="grid grid-cols-[60px_1fr] border border-gray-200 rounded-lg overflow-hidden">
      <div className="bg-gray-50 border-b border-gray-200 p-2 text-center">
        <div className="text-xs text-gray-500">{currentDate.toLocaleDateString([], { weekday: 'short' })}</div>
        <div className={`text-lg font-bold ${isSameDay(currentDate, new Date()) ? 'text-blue-600' : 'text-gray-900'}`}>
          {currentDate.getDate()}
        </div>
      </div>
      <div className="bg-gray-50 border-b border-l border-gray-200 px-3 py-2 flex items-center">
        <span className="text-sm font-medium text-gray-700">
          {currentDate.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
        </span>
        <span className="ml-2 text-xs text-gray-400">{visibleEvents.length} event{visibleEvents.length !== 1 ? 's' : ''}</span>
      </div>

      {HOURS.map(hour => {
        const hourEvents = allEvents.filter(ev => {
          if (!ev.activityDate) return false;
          const d = new Date(ev.activityDate);
          return isSameDay(d, currentDate) && d.getHours() === hour;
        });
        return (
          <React.Fragment key={hour}>
            <div className="border-t border-gray-100 px-1 text-[10px] text-gray-400 text-right pr-2 h-16 flex items-start pt-0.5">
              {hour === 0 ? '12 AM' : hour < 12 ? `${hour} AM` : hour === 12 ? '12 PM' : `${hour - 12} PM`}
            </div>
            <div className="border-t border-l border-gray-100 h-16 px-2 py-0.5 space-y-0.5">
              {hourEvents.map(ev => (
                <EventCard key={ev.id} event={ev} />
              ))}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );

  // Agenda list (grouped by date)
  const AgendaView = () => {
    const grouped = useMemo(() => {
      const groups: Record<string, Activity[]> = {};
      visibleEvents.forEach(ev => {
        const key = ev.activityDate ? new Date(ev.activityDate).toDateString() : 'No date';
        if (!groups[key]) groups[key] = [];
        groups[key].push(ev);
      });
      return groups;
    }, [visibleEvents]);

    if (visibleEvents.length === 0) {
      return (
        <div className="text-center py-12 text-gray-400">
          <Calendar className="h-10 w-10 mx-auto mb-3 text-gray-300" />
          <p className="text-sm font-medium text-gray-500">No upcoming events</p>
          <p className="text-xs text-gray-400 mt-1">Click "Sync Calendar" to pull events from Outlook</p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {Object.entries(grouped).map(([dateStr, events]) => {
          const d = new Date(dateStr);
          const isToday = isSameDay(d, new Date());
          return (
            <div key={dateStr}>
              <div className={`flex items-center gap-2 mb-2 px-2 ${isToday ? 'text-blue-600' : 'text-gray-500'}`}>
                <div className={`w-10 h-10 rounded-lg flex flex-col items-center justify-center text-xs ${isToday ? 'bg-blue-100' : 'bg-gray-100'}`}>
                  <span className="font-bold text-sm leading-none">{d.getDate()}</span>
                  <span className="text-[10px] uppercase">{d.toLocaleDateString([], { weekday: 'short' })}</span>
                </div>
                <div>
                  <p className={`text-xs font-medium ${isToday ? 'text-blue-600' : 'text-gray-700'}`}>
                    {isToday ? 'Today' : d.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' })}
                  </p>
                  <p className="text-[10px] text-gray-400">{events.length} event{events.length !== 1 ? 's' : ''}</p>
                </div>
              </div>
              <div className="space-y-1 ml-2 border-l-2 border-gray-200 pl-4">
                {events.map(event => {
                  const meta = event.metadata || {};
                  const myResponse = meta.my_response || 'none';
                  return (
                    <div
                      key={event.id}
                      className={`flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer transition-colors hover:bg-gray-50 border border-gray-100`}
                      onClick={() => setSelectedEvent(event)}
                    >
                      <div className={`w-1 h-8 rounded-full flex-shrink-0 ${
                        myResponse === 'accepted' ? 'bg-green-400' :
                        myResponse === 'declined' ? 'bg-red-400' :
                        myResponse === 'tentativelyAccepted' ? 'bg-amber-400' :
                        meta.is_organizer ? 'bg-purple-400' : 'bg-gray-300'
                      }`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{event.title}</p>
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <span>{event.activityDate ? formatTime(new Date(event.activityDate)) : ''}</span>
                          {meta.location && <span className="flex items-center gap-0.5"><MapPin className="h-3 w-3" />{meta.location}</span>}
                          {meta.meeting_link && <span className="flex items-center gap-0.5 text-blue-500"><Video className="h-3 w-3" />Online</span>}
                          {meta.attendees && meta.attendees.length > 0 && <span className="flex items-center gap-0.5"><Users className="h-3 w-3" />{meta.attendees.length}</span>}
                        </div>
                      </div>
                      <span className={`px-2 py-0.5 text-[10px] font-medium rounded-full ${
                        myResponse === 'accepted' ? 'bg-green-100 text-green-700' :
                        myResponse === 'declined' ? 'bg-red-100 text-red-700' :
                        myResponse === 'tentativelyAccepted' ? 'bg-amber-100 text-amber-700' :
                        meta.is_organizer ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {meta.is_organizer ? 'Organizer' : responseLabel(myResponse)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Event detail modal
  const EventDetail = ({ event, onClose }: { event: Activity; onClose: () => void }) => {
    const meta = event.metadata || {};
    const myResponse = meta.my_response || 'none';
    const linkedOpp = event.opportunityId ? opportunities.find(o => o.id === event.opportunityId) : null;

    return (
      <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" onClick={onClose}>
        <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-2 sm:mx-4 max-h-[90vh] sm:max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
          <div className="flex items-center justify-between px-4 sm:px-5 py-3 sm:py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
            <h3 className="text-base font-semibold text-gray-900 truncate mr-2">{event.title}</h3>
            <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded flex-shrink-0"><X className="h-4 w-4" /></button>
          </div>
          <div className="px-4 sm:px-5 py-4 space-y-4">
            {/* Date/Time */}
            <div className="flex items-center gap-2 text-sm text-gray-700">
              <Clock className="h-4 w-4 text-gray-400" />
              {event.activityDate ? (
                <span>{new Date(event.activityDate).toLocaleString([], { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
              ) : 'No date'}
            </div>

            {meta.organizer_name && (
              <div className="text-sm text-gray-600">
                <span className="text-gray-400">Organizer: </span>{meta.organizer_name} ({meta.organizer_email})
              </div>
            )}

            {meta.location && (
              <div className="flex items-center gap-2 text-sm"><MapPin className="h-4 w-4 text-gray-400" />{meta.location}</div>
            )}

            {meta.meeting_link && (
              <a href={meta.meeting_link} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100">
                <Video className="h-4 w-4" />Join Meeting<ExternalLink className="h-3 w-3" />
              </a>
            )}

            {event.description && (
              <div className="text-sm text-gray-600 bg-gray-50 rounded-lg p-3 border border-gray-200">{event.description}</div>
            )}

            {/* Attendees */}
            {meta.attendees && meta.attendees.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wider">Attendees ({meta.attendees.length})</p>
                <div className="space-y-1.5">
                  {meta.attendees.map((att, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      {responseIcon(att.response)}
                      <span className="font-medium text-gray-700">{att.name || att.email}</span>
                      {att.name && att.email && <span className="text-gray-400 text-xs">{att.email}</span>}
                      <span className={`ml-auto px-1.5 py-0.5 rounded text-[10px] ${
                        att.response === 'accepted' ? 'bg-green-50 text-green-600' :
                        att.response === 'declined' ? 'bg-red-50 text-red-600' :
                        att.response === 'tentativelyAccepted' ? 'bg-amber-50 text-amber-600' :
                        'bg-gray-50 text-gray-500'
                      }`}>{responseLabel(att.response)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {linkedOpp && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-2 text-sm">
                <span className="text-green-600 font-medium">Linked: </span>
                <span className="text-green-800">{linkedOpp.name} ({linkedOpp.company})</span>
              </div>
            )}

            {/* RSVP Actions — only show for events where user is invited (not organizer, has attendees) */}
            {!meta.is_organizer && meta.attendees && meta.attendees.length > 0 && (
              <div className="border-t border-gray-200 pt-3">
                <p className="text-xs font-semibold text-gray-500 mb-2">Your Response</p>
                <div className="flex gap-2">
                  {(['accept', 'tentativelyAccept', 'decline'] as const).map(r => {
                    const label = r === 'accept' ? 'Accept' : r === 'tentativelyAccept' ? 'Tentative' : 'Decline';
                    const icon = r === 'accept' ? <Check className="h-4 w-4" /> : r === 'tentativelyAccept' ? <HelpCircle className="h-4 w-4" /> : <X className="h-4 w-4" />;
                    const mapped = r === 'accept' ? 'accepted' : r === 'tentativelyAccept' ? 'tentativelyAccepted' : 'declined';
                    const isActive = myResponse === mapped;
                    const colors = r === 'accept'
                      ? (isActive ? 'bg-green-100 border-green-300 text-green-700' : 'border-gray-200 text-gray-600 hover:bg-green-50')
                      : r === 'tentativelyAccept'
                      ? (isActive ? 'bg-amber-100 border-amber-300 text-amber-700' : 'border-gray-200 text-gray-600 hover:bg-amber-50')
                      : (isActive ? 'bg-red-100 border-red-300 text-red-700' : 'border-gray-200 text-gray-600 hover:bg-red-50');
                    return (
                      <button key={r}
                        onClick={() => respondToEvent(event.id, r)}
                        disabled={responding === event.id}
                        className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm rounded-lg border font-medium ${colors}`}>
                        {responding === event.id ? <Loader2 className="h-4 w-4 animate-spin" /> : icon}{label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {meta.web_link && (
              <a href={meta.web_link} target="_blank" rel="noopener noreferrer"
                className="flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 border border-gray-200">
                <ExternalLink className="h-3 w-3" />Open in Outlook
              </a>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col">
      {/* Toolbar */}
      <div className="flex items-center gap-2 mb-3 flex-wrap overflow-x-auto">
        {/* View mode */}
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
          {([['agenda', 'Agenda', List], ['week', 'Week', LayoutGrid], ['day', 'Day', Calendar]] as const).map(([mode, label, Icon]) => (
            <button
              key={mode}
              onClick={() => { setViewMode(mode as ViewMode); setSelectedEvent(null); }}
              className={`flex items-center gap-1 px-3 py-1 text-xs rounded-md ${
                viewMode === mode ? 'bg-white text-gray-900 shadow-sm font-medium' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Icon className="h-3.5 w-3.5" />
              {label}
            </button>
          ))}
        </div>

        {/* Date navigation (for week/day views) */}
        {viewMode !== 'agenda' && (
          <div className="flex items-center gap-1">
            <button onClick={() => navigateDate(-1)} className="p-1 hover:bg-gray-100 rounded"><ChevronLeft className="h-4 w-4" /></button>
            <button onClick={() => setCurrentDate(new Date())} className="px-2 py-1 text-xs text-blue-600 hover:bg-blue-50 rounded font-medium">Today</button>
            <button onClick={() => navigateDate(1)} className="p-1 hover:bg-gray-100 rounded"><ChevronRight className="h-4 w-4" /></button>
            <span className="text-sm font-medium text-gray-700 ml-1">
              {viewMode === 'day'
                ? formatDate(currentDate)
                : `${formatDate(startOfWeek(currentDate))} – ${formatDate(addDays(startOfWeek(currentDate), 6))}`
              }
            </span>
          </div>
        )}

        <div className="flex-1" />

        <span className="text-xs text-gray-400">{allEvents.length} total events</span>

        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-1 px-2.5 py-1 text-xs border border-blue-300 text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100"
        >
          <Plus className="h-3 w-3" />Create Event
        </button>

        <button
          onClick={syncCalendar}
          disabled={syncing}
          className="flex items-center gap-1 px-2.5 py-1 text-xs border border-purple-300 text-purple-700 bg-purple-50 rounded-lg hover:bg-purple-100 disabled:opacity-50"
        >
          <RefreshCw className={`h-3 w-3 ${syncing ? 'animate-spin' : ''}`} />
          {syncing ? 'Syncing...' : 'Sync Calendar'}
        </button>
      </div>

      {/* Pending RSVP notification banner */}
      {pendingRsvpEvents.length > 0 && (
        <div className="mb-3 bg-amber-50 border border-amber-200 rounded-lg overflow-hidden">
          <div className="flex items-center gap-2 px-3 py-2">
            <div className="relative flex-shrink-0">
              <Bell className="h-4 w-4 text-amber-600" />
              <span className="absolute -top-1 -right-1.5 bg-amber-500 text-white text-[9px] font-bold rounded-full h-3.5 min-w-[14px] flex items-center justify-center px-0.5">
                {pendingRsvpEvents.length}
              </span>
            </div>
            <span className="text-xs font-medium text-amber-800">
              {pendingRsvpEvents.length} calendar invite{pendingRsvpEvents.length !== 1 ? 's' : ''} pending your response
            </span>
          </div>
          <div className="border-t border-amber-200 divide-y divide-amber-100 max-h-40 overflow-y-auto">
            {pendingRsvpEvents.map(ev => {
              const meta = ev.metadata || {};
              return (
                <div
                  key={ev.id}
                  className="flex items-center gap-3 px-3 py-2 hover:bg-amber-100/50 cursor-pointer transition-colors"
                  onClick={() => setSelectedEvent(ev)}
                >
                  <Clock className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-gray-900 truncate">{ev.title}</p>
                    <p className="text-[10px] text-gray-500">
                      {ev.activityDate ? new Date(ev.activityDate).toLocaleString([], { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                      {meta.organizer_name ? ` · ${meta.organizer_name}` : ''}
                    </p>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <button
                      onClick={(e) => { e.stopPropagation(); respondToEvent(ev.id, 'accept'); }}
                      disabled={responding === ev.id}
                      className="p-1 rounded hover:bg-green-200 text-green-600" title="Accept"
                    >
                      <Check className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); respondToEvent(ev.id, 'tentativelyAccept'); }}
                      disabled={responding === ev.id}
                      className="p-1 rounded hover:bg-amber-200 text-amber-600" title="Tentative"
                    >
                      <HelpCircle className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); respondToEvent(ev.id, 'decline'); }}
                      disabled={responding === ev.id}
                      className="p-1 rounded hover:bg-red-200 text-red-600" title="Decline"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); setRespondedIds(prev => new Set(prev).add(ev.id)); }}
                      className="p-1 rounded hover:bg-gray-200 text-gray-400 ml-0.5" title="Dismiss"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Views */}
      {viewMode === 'agenda' && <AgendaView />}
      {viewMode === 'week' && <div className="overflow-x-auto"><WeekView /></div>}
      {viewMode === 'day' && <DayView />}

      {/* Event detail modal */}
      {selectedEvent && <EventDetail event={selectedEvent} onClose={() => setSelectedEvent(null)} />}

      {/* Create event modal */}
      <CreateEventModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreated={() => { syncCalendar(); }}
        defaultDate={viewMode === 'day' ? currentDate : undefined}
      />
    </div>
  );
}
